import styled, { css } from "styled-components";
import { designTokens } from '../../ui/foundation/DesignTokens';

// Main card wrapper
export const FollowUpCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: 16px;
  padding: 16px;
  margin: 0 16px 16px 16px;
`;

// Card header with icon, title, and badge
export const FollowUpCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

export const FollowUpIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: ${designTokens.colors.primary[50]};
  border: 1px solid ${designTokens.colors.primary[100]};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
    color: ${designTokens.colors.primary[600]};
  }
`;

export const FollowUpCardTitleSection = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FollowUpCardTitle = styled.h4`
  font-family: ${designTokens.fonts.heading};
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin: 0 0 2px 0;
`;

export const AutopilotBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${designTokens.colors.primary[50]};
  border: 1px solid ${designTokens.colors.primary[200]};
  border-radius: 999px;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${designTokens.colors.primary[600]};
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }
`;

// Description and toggle row
export const FollowUpDescriptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

export const FollowUpDescription = styled.div`
  flex: 1;
`;

export const FollowUpDescriptionTitle = styled.p`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  font-weight: 500;
  color: ${designTokens.colors.gray[700]};
  margin: 0 0 4px 0;
`;

export const FollowUpDescriptionText = styled.p`
  font-family: ${designTokens.fonts.body};
  font-size: 0.75rem;
  color: ${designTokens.colors.gray[500]};
  margin: 0;
  line-height: 1.4;
`;

// Toggle switch
export const ToggleSwitch = styled.button`
  width: 48px;
  height: 28px;
  background: ${props => props.active ? designTokens.colors.primary[500] : designTokens.colors.gray[200]};
  border: none;
  border-radius: 999px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${props => props.active ? '23px' : '3px'};
    width: 22px;
    height: 22px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

// Scheduled items list
export const ScheduledItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ScheduledItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  background: ${designTokens.colors.gray[50]};
  border: 1px solid ${designTokens.colors.gray[100]};
  border-radius: 12px;
  gap: 12px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: ${designTokens.colors.gray[100]};
    border-color: ${designTokens.colors.gray[200]};
  }
`;

export const ScheduledItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ScheduledItemTime = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-size: 0.875rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: 2px;
`;

export const ScheduledItemDescription = styled.div`
  font-family: ${designTokens.fonts.body};
  font-size: 0.75rem;
  color: ${designTokens.colors.gray[500]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ScheduledItemBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${props => props.variant === 'important' ? designTokens.colors.warning[100] : designTokens.colors.primary[50]};
  border: 1px solid ${props => props.variant === 'important' ? designTokens.colors.warning[200] : designTokens.colors.primary[200]};
  border-radius: 999px;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${props => props.variant === 'important' ? designTokens.colors.warning[700] : designTokens.colors.primary[600]};
  flex-shrink: 0;
`;

// Action buttons
export const FollowUpActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CreateFollowUpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${(props) => props.variant === 'cancel' ? 'white' : designTokens.colors.primary[500]};
  border: ${(props) => props.variant === 'cancel' ? `1px solid ${designTokens.colors.gray[300]}` : 'none'};
  border-radius: 12px;
  color: ${(props) => props.variant === 'cancel' ? designTokens.colors.gray[600] : 'white'};
  font-family: ${designTokens.fonts.heading};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: ${(props) => props.variant === 'cancel' ? designTokens.colors.gray[50] : designTokens.colors.primary[600]};
    color: ${(props) => props.variant === 'cancel' ? designTokens.colors.error[500] : 'white'};
    border-color: ${(props) => props.variant === 'cancel' ? designTokens.colors.error[300] : 'transparent'};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const EditRulesButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: 12px;
  color: ${designTokens.colors.gray[700]};
  font-family: ${designTokens.fonts.heading};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover {
    background: ${designTokens.colors.gray[50]};
    border-color: ${designTokens.colors.gray[300]};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// Quick action cards grid
export const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 0 16px 16px 16px;
`;

export const QuickActionCard = styled.button`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.18s ease;
  text-align: left;

  &:hover {
    border-color: ${designTokens.colors.primary[200]};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

export const QuickActionIcon = styled.div`
  width: 36px;
  height: 36px;
  background: ${props => props.variant === 'primary' ? designTokens.colors.primary[50] : designTokens.colors.gray[100]};
  border: 1px solid ${props => props.variant === 'primary' ? designTokens.colors.primary[100] : designTokens.colors.gray[200]};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.variant === 'primary' ? designTokens.colors.primary[600] : designTokens.colors.gray[500]};
  }
`;

export const QuickActionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const QuickActionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${designTokens.colors.gray[900]};
  margin-bottom: 2px;
`;

export const QuickActionBadge = styled.span`
  display: inline-flex;
  padding: 2px 6px;
  background: ${props => {
    switch(props.variant) {
      case 'task': return designTokens.colors.primary[50];
      case 'auto': return designTokens.colors.primary[50];
      case 'rules': return designTokens.colors.gray[100];
      case 'campaign': return designTokens.colors.primary[50];
      default: return designTokens.colors.gray[100];
    }
  }};
  border-radius: 4px;
  font-family: ${designTokens.fonts.heading};
  font-size: 0.625rem;
  font-weight: 600;
  color: ${props => {
    switch(props.variant) {
      case 'task': return designTokens.colors.primary[600];
      case 'auto': return designTokens.colors.primary[600];
      case 'rules': return designTokens.colors.gray[600];
      case 'campaign': return designTokens.colors.primary[600];
      default: return designTokens.colors.gray[600];
    }
  }};
`;

export const QuickActionDescription = styled.div`
  font-family: ${designTokens.fonts.body};
  font-size: 0.6875rem;
  color: ${designTokens.colors.gray[500]};
  line-height: 1.3;
`;

// Legacy - keeping for backwards compatibility
export const FollowUpContainer = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  position: relative;
`;

export const FollowUpTitle = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.8125rem;
  margin-left: 10px;
  color: ${designTokens.colors.gray[500]};
  width: 5.5rem;
`;

export const FollowUpDataTitle = styled.section`
  overflow: hidden;
  color: ${designTokens.colors.gray[600]};
  margin-bottom: 8px;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-family: ${designTokens.fonts.body};
`;

export const FollowUpDataContainer = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FollowUpData = styled.div`
  display: flex;
  color: ${designTokens.colors.gray[600]};
  align-items: center;
`;

export const FollowUpDate = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.8125rem;
`;

export const FollowUpDateInputContainer = styled.div`
  margin-bottom: 24px;
  ${(props) =>
    props.followDateCampaign &&
    css`
      margin-left: 21px;
    `}
`;

export const FollowUpDateInputTitle = styled.div`
  font-family: ${designTokens.fonts.heading};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 10px;
  width: 100%;
  color: ${designTokens.colors.gray[700]};
`;

export const FollowUpDatePickerContainer = styled.div`
  width: 100%;
  display: flex;
`;

export const FollowUpTextArea = styled.textarea`
  padding: 12px;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.md};
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  width: 100%;
  height: 120px;
  resize: vertical;
  color: ${designTokens.colors.gray[900]};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }

  &::placeholder {
    color: ${designTokens.colors.gray[400]};
  }
`;

export const FollowUpSubmitButton = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

export const DateInputContainer = styled.button`
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.md};
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  width: 100%;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${designTokens.colors.gray[300]};
  }

  &:focus {
    outline: none;
    border-color: ${designTokens.colors.primary[500]};
    box-shadow: 0 0 0 3px rgba(44, 95, 78, 0.1);
  }
`;

export const DateInputLabels = styled.div`
  display: flex;
  align-items: center;
`;

export const DateInputText = styled.span`
  font-family: ${designTokens.fonts.body};
  font-size: 0.875rem;
  margin-left: 10px;
  color: ${designTokens.colors.gray[700]};
`;

export const FollowUpTitlleContainer = styled.div`
  display: flex;
  padding-right: 12px;
`;
